import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard,
  Search,
  Users,
  Settings,
  LogOut,
  Activity,
  Clock,
  AlertTriangle,
  CheckCircle
} from "lucide-react";

// Pages
import DashboardPage from "./pages/Dashboard";
import BrowsePage from "./pages/Browse";
import SourcesPage from "./pages/Sources";
import SettingsPage from "./pages/Settings";
import LoginPage from "./pages/Login";
import PublicFeedPage from "./pages/PublicFeed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Sidebar Component
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: Search, label: "Browse", path: "/admin/browse" },
    { icon: Users, label: "Sources", path: "/admin/sources" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
  ];

  return (
    <aside className="fixed left-0 top-0 h-screen w-56 bg-[#12121a] border-r border-[#2a2a3a] flex flex-col z-50" data-testid="sidebar">
      {/* Logo */}
      <div className="p-5 border-b border-[#2a2a3a]">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 22 22" className="w-6 h-6 flex-shrink-0" fill="none">
            <rect x="1" y="1" width="9" height="9" rx="2.5" fill="#10b981"/>
            <rect x="12" y="1" width="9" height="9" rx="2.5" fill="#10b981" opacity="0.45"/>
            <rect x="1" y="12" width="9" height="9" rx="2.5" fill="#10b981" opacity="0.45"/>
            <rect x="12" y="12" width="9" height="9" rx="2.5" fill="#10b981" opacity="0.2"/>
          </svg>
          <span className="font-heading font-semibold text-base tracking-tight text-[#e0e0e8]">
            bitz<span className="text-[#10b981]">&</span>blockz
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors duration-150 mb-1
                ${isActive 
                  ? 'bg-[#6ee7b7]/10 text-[#6ee7b7]' 
                  : 'text-[#7a7a8e] hover:text-[#e0e0e8] hover:bg-[#1a1a26]'
                }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-[#2a2a3a]">
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium text-[#7a7a8e] hover:text-[#ef4444] hover:bg-[#ef4444]/10 transition-colors duration-150"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </aside>
  );
};

// Layout Component
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Sidebar />
      <main className="ml-56 p-6">
        {children}
      </main>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-[#7a7a8e]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token
      api.get('/auth/me')
        .then(() => setIsAuthenticated(true))
        .catch(() => {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', response.data.token);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right" 
          theme="dark" 
          toastOptions={{
            style: {
              background: '#12121a',
              border: '1px solid #2a2a3a',
              color: '#e0e0e8',
            },
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicFeedPage />} />
          <Route path="/feed" element={<PublicFeedPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/admin/browse" element={<ProtectedRoute><BrowsePage /></ProtectedRoute>} />
          <Route path="/admin/sources" element={<ProtectedRoute><SourcesPage /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
